<script setup lang="ts">
  import { computed, ref, useTemplateRef, watch } from 'vue'

  const props = defineProps<{
    label?: string,
    aspectX: number,
    aspectY: number,
    imageCropperLabel: string,
    initialCenterSize?: 'contain' | 'fill',
    currentImage?: string,
    skipCrop?: boolean,
  }>()

  const emit = defineEmits<{
    (e: 'delete'): void,
  }>()

  const modelBlob = defineModel<Blob | null>('blob', { default: null })
  const modelSrc = ref<string | null>(props.currentImage ?? null)

  const refImgCropper = useTemplateRef('refImgCropper')
  const files = ref<File[]>([])
  watch(files, () => {
    if (files.value[0]) {
      const file = files.value[0]
      modelSrc.value = URL.createObjectURL(file)

      if (props.skipCrop) {
        modelBlob.value = file
      } else {
        openImgCropper()
      }

      files.value = []
    }
  })

  const previewSrc = computed(() => {
    if (modelBlob.value) {
      return URL.createObjectURL(modelBlob.value)
    }
    return props.currentImage
  })

  const openImgCropper = () => {
    refImgCropper.value?.open()
  }

  const deleteImage = () => {
    modelBlob.value = null
    modelSrc.value = null
    emit('delete')
  }
</script>

<template>
  <app-input-file
    v-model="files"
    accept="image/*">
    <template #default="{ open }">
      <div class="group relative leading-0">
        <div class="absolute inset-0 border"></div>
        <app-button
          :style="{
            width: '100%',
            height: 'auto',
            aspectRatio: `${aspectX}/${aspectY}`,
          }"
          @click="open()">
          <app-image
            v-if="previewSrc"
            class="object-cover"
            :style="{
              width: '100%',
              height: 'auto',
              aspectRatio: `${aspectX}/${aspectY}`,
            }"
            :src="previewSrc" />
          <!-- plus icon size depends to the container -->
          <div
            v-else
            class="size-full flex-center">
            <!-- create square at the center, plus size is 2/5 (40%) of the container size -->
            <div class="relative aspect-square h-full flex-center">
              <div class="absolute h-0.5 w-2/9 bg-gray-300"></div>
              <div class="absolute h-2/9 w-0.5 bg-gray-300"></div>
            </div>
          </div>
        </app-button>

        <!-- edit, delete, change image button -->
        <div
          v-if="currentImage || modelBlob"
          class="absolute hidden group-hover:block pointer-events-none inset-0 bg-black/70"></div>
        <div class="absolute bottom-2 right-2 hidden items-center group-hover:flex space-x-1">
          <app-button
            v-if="currentImage"
            class="btn-low-priority btn-edit"
            title="Ganti Gambar"
            @click="open()">
            <fa-icon icon="i-far-folder-open" />
          </app-button>
          <app-button
            v-if="modelBlob || currentImage"
            class="btn-low-priority btn-edit"
            title="Crop Gambar"
            @click="openImgCropper()">
            <fa-icon icon="i-far-crop" />
          </app-button>
          <app-button
            v-if="currentImage || modelBlob"
            class="btn-low-priority btn-edit"
            title="Hapus Gambar"
            @click="deleteImage()">
            <fa-icon icon="i-fas-xmark" />
          </app-button>
        </div>
      </div>
    </template>
  </app-input-file>

  <app-image-cropper
    ref="refImgCropper"
    v-model="modelBlob"
    :title="imageCropperLabel"
    :aspect-x="aspectX"
    :aspect-y="aspectY"
    :initial-center-size="initialCenterSize"
    :src="modelSrc ?? ''" />
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .btn-edit {
    @apply bg-white !size-6 !p-0 !text-xs flex items-center justify-center rounded;
  }
</style>
